import { Button, Checkbox, Label } from "@/components/ui";
import type { SurveyDraftQuestion } from "./SurveyQuestionBuilder";

interface SurveyQuestionDisplayRuleProps {
  question: SurveyDraftQuestion;
  previousQuestions: SurveyDraftQuestion[];
  labels: Map<string, string>;
  disabled: boolean;
  onRemoveCondition: () => void;
  onOptionChange: (optionId: string, checked: boolean) => void;
}

export function SurveyQuestionDisplayRule({
  question,
  previousQuestions,
  labels,
  disabled,
  onRemoveCondition,
  onOptionChange,
}: SurveyQuestionDisplayRuleProps) {
  const parent = previousQuestions.find(
    (candidate) => candidate.key === question.displayWhen?.questionId,
  );
  return (
    <div className="bg-muted/30 space-y-3 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-foreground text-sm font-medium">
            {parent
              ? `Show this question when Question ${labels.get(parent.key)} has any of these answers:`
              : "This question's condition is unavailable."}
          </p>
          {parent?.prompt.trim() ? (
            <p className="text-muted-foreground mt-1 text-sm">
              {parent.prompt.trim()}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onRemoveCondition}
        >
          Remove condition
        </Button>
      </div>
      {parent ? (
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {parent.options.map((option, index) => {
            const checkboxId = `display-${question.key}-${option.id}`;
            return (
              <div key={option.id} className="flex items-center gap-2">
                <Checkbox
                  id={checkboxId}
                  className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                  disabled={disabled}
                  checked={
                    question.displayWhen?.optionIds.includes(option.id) ?? false
                  }
                  onCheckedChange={(checked) => {
                    onOptionChange(option.id, checked === true);
                  }}
                />
                <Label htmlFor={checkboxId} className="text-foreground text-sm">
                  {option.label.trim() || `Option ${index + 1}`}
                </Label>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
